import type { User } from "@api/types";
import posthog from "posthog-js";

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
/** Managed reverse proxy (e.g. https://t.sendhelios.com) — bypasses ad blockers. */
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
/** PostHog app URL for toolbar / links. Keep separate from the proxy api_host. */
const posthogUiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ?? "https://us.posthog.com";
let initialized = false;

export const isConfigured = Boolean(posthogKey);

export function init() {
  if (initialized) {
    return;
  }

  if (!posthogKey) {
    return;
  }

  try {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      ui_host: posthogUiHost,
      // Managed proxy serves versioned /static/* assets; keep feature scripts in sync.
      strict_script_versioning: true,
      person_profiles: "identified_only",
      capture_pageview: false,
      loaded: (posthog) => {
        if (process.env.NODE_ENV === "development") posthog.debug();
      },
    });
    initialized = true;
  } catch (err) {
    console.error("[posthog] init:", err);
  }
}

export function identify(user: User) {
  if (!posthogKey) {
    return;
  }
  try {
    posthog.identify(user.id, {
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
    });
  } catch (err) {
    console.error("[posthog] identify:", err);
  }
}

let flagsGymKey: string | null = null;

/**
 * Sends the active gym and org with every flag evaluation so release
 * conditions can target `gym` or `org` ids (multi-location orgs cannot be
 * targeted at the user or org level alone). The properties ride along on the
 * flags request, so a gym switch re-evaluates without waiting on ingestion.
 * Repeated calls for the same gym are no-ops; `null` clears the properties.
 */
export function setGymForFlags(gym: { id: string; org: string } | null) {
  if (!posthogKey) {
    return;
  }
  const key = gym ? `${gym.org}:${gym.id}` : null;
  if (key === flagsGymKey) {
    return;
  }
  flagsGymKey = key;
  try {
    if (gym) {
      posthog.setPersonPropertiesForFlags({ gym: gym.id, org: gym.org }, true);
    } else {
      posthog.resetPersonPropertiesForFlags(true);
    }
  } catch (err) {
    console.error("[posthog] setGymForFlags:", err);
  }
}

export function reset() {
  flagsGymKey = null;
  posthog.reset();
}

export const client = posthog;
